import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { FlaskConical, LogOut, Mail, Send, Users } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const inputClass =
  "w-full rounded-full border border-crave-border bg-crave-card px-6 py-3.5 text-sm text-crave-cream placeholder:text-crave-muted focus:border-crave-gold focus:outline-none";

const PlatformPill = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-300 ${
      active
        ? "bg-crave-gold text-crave-bg"
        : "border border-crave-border text-crave-muted hover:border-crave-gold/50 hover:text-crave-cream"
    }`}
  >
    {children}
  </button>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [testers, setTesters] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [beta, setBeta] = useState({ platform: "android", link: "" });
  const [betaStatus, setBetaStatus] = useState("idle");
  const [betaMsg, setBetaMsg] = useState("");

  const api = async (path, options = {}) => {
    const res = await fetch(`${API}/api${path}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      ...options,
    });
    if (!res.ok) {
      const d = await res.json().catch(() => null);
      throw new Error(formatDetail(d?.detail));
    }
    return res.json();
  };

  const loadData = useCallback(async () => {
    const [t, w] = await Promise.all([api("/admin/testers"), api("/admin/waitlist")]);
    setTesters(t.testers);
    setWaitlist(w.waitlist);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setUser(await api("/auth/me"));
      } catch {
        try {
          await api("/auth/refresh", { method: "POST" });
          setUser(await api("/auth/me"));
        } catch {
          setUser(false);
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (user) loadData().catch(() => {});
  }, [user, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      setUser(await api("/auth/login", { method: "POST", body: JSON.stringify(loginForm) }));
    } catch (err) {
      setLoginError(err.message);
    }
  };

  const logout = async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {}
    setUser(false);
  };

  const sendBeta = async (e) => {
    e.preventDefault();
    setBetaStatus("loading");
    setBetaMsg("");
    try {
      const data = await api("/admin/send-beta", { method: "POST", body: JSON.stringify(beta) });
      setBetaStatus("done");
      setBetaMsg(
        `Sent to ${data.sent} tester${data.sent === 1 ? "" : "s"}` +
          (data.skipped ? ` · ${data.skipped} already emailed` : "") +
          (data.failed ? ` · ${data.failed} failed` : "")
      );
      loadData().catch(() => {});
    } catch (err) {
      setBetaStatus("error");
      setBetaMsg(err.message);
    }
  };

  const androidCount = testers.filter((t) => t.platform !== "ios").length;
  const iosCount = testers.filter((t) => t.platform === "ios").length;

  return (
    <div className="min-h-screen bg-crave-bg font-sans text-crave-cream">
      <Helmet>
        <title>Admin — Foodie Crave</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="border-b border-crave-border">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
          <Logo testId="admin-logo" />
          {user && (
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 text-sm font-medium text-crave-muted transition-colors hover:text-crave-gold"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Log out
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-14">
        {user === null ? (
          <p className="text-center text-sm text-crave-muted">Loading…</p>
        ) : user === false ? (
          <div className="mx-auto max-w-md rounded-3xl border border-crave-border bg-crave-card p-8">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-crave-gold">Admin</p>
            <h1 className="mt-3 font-display text-3xl font-medium tracking-tight">Tester signups</h1>
            <form onSubmit={login} data-testid="admin-login-form" className="mt-8 space-y-3">
              <label htmlFor="admin-email" className="sr-only">Email</label>
              <input
                id="admin-email"
                type="email"
                required
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                placeholder="Admin email"
                data-testid="admin-email-input"
                className={inputClass}
              />
              <label htmlFor="admin-password" className="sr-only">Password</label>
              <input
                id="admin-password"
                type="password"
                required
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Password"
                data-testid="admin-password-input"
                className={inputClass}
              />
              {loginError && (
                <p data-testid="admin-login-error" className="text-sm text-crave-gold">{loginError}</p>
              )}
              <button
                type="submit"
                data-testid="admin-login-submit"
                className="w-full rounded-full bg-crave-gold px-6 py-3.5 text-sm font-bold text-crave-bg transition-colors hover:bg-crave-cream"
              >
                Log in
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.28em] text-crave-gold">Admin</p>
                <h1 className="mt-3 font-display text-3xl font-medium tracking-tight sm:text-4xl">
                  Signups
                </h1>
              </div>
              <div className="flex flex-wrap gap-3" data-testid="admin-stats">
                <span className="flex items-center gap-2 rounded-full border border-crave-border bg-crave-card px-4 py-2 text-sm text-crave-muted">
                  <Users className="h-4 w-4 text-crave-gold" aria-hidden="true" />
                  {testers.length} testers
                </span>
                <span className="rounded-full border border-crave-border bg-crave-card px-4 py-2 text-sm text-crave-muted">
                  {androidCount} Android
                </span>
                <span className="rounded-full border border-crave-border bg-crave-card px-4 py-2 text-sm text-crave-muted">
                  {iosCount} iOS
                </span>
                <span className="flex items-center gap-2 rounded-full border border-crave-border bg-crave-card px-4 py-2 text-sm text-crave-muted">
                  <Mail className="h-4 w-4 text-crave-gold" aria-hidden="true" />
                  {waitlist.length} waitlist
                </span>
              </div>
            </div>

            <h2 className="mt-12 font-display text-2xl font-medium tracking-tight">Beta testers</h2>
            <div className="mt-5 overflow-hidden rounded-3xl border border-crave-border bg-crave-card">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="testers-table">
                  <thead>
                    <tr className="border-b border-crave-border text-xs uppercase tracking-[0.14em] text-crave-muted">
                      <th className="px-6 py-4 font-semibold">Name</th>
                      <th className="px-6 py-4 font-semibold">Email</th>
                      <th className="px-6 py-4 font-semibold">Platform</th>
                      <th className="px-6 py-4 font-semibold">Device</th>
                      <th className="px-6 py-4 font-semibold">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-10 text-center text-crave-muted">
                          No testers yet.
                        </td>
                      </tr>
                    ) : (
                      testers.map((t, i) => (
                        <tr
                          key={t.email}
                          data-testid={`tester-row-${i}`}
                          className="border-b border-crave-border/60 last:border-0"
                        >
                          <td className="px-6 py-4 font-medium text-crave-cream">{t.name}</td>
                          <td className="px-6 py-4 text-crave-muted">{t.email}</td>
                          <td className="px-6 py-4">
                            <span className="rounded-full bg-crave-gold/15 px-3 py-1 text-xs font-semibold text-crave-gold">
                              {t.platform === "ios" ? "iOS" : "Android"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-crave-muted">{t.device || "—"}</td>
                          <td className="px-6 py-4 text-crave-muted">
                            {t.joined_at ? new Date(t.joined_at).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <h2 className="mt-12 font-display text-2xl font-medium tracking-tight">Waitlist</h2>
            <div className="mt-5 overflow-hidden rounded-3xl border border-crave-border bg-crave-card">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="waitlist-table">
                  <thead>
                    <tr className="border-b border-crave-border text-xs uppercase tracking-[0.14em] text-crave-muted">
                      <th className="px-6 py-4 font-semibold">Email</th>
                      <th className="px-6 py-4 font-semibold">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-6 py-10 text-center text-crave-muted">
                          No waitlist signups yet.
                        </td>
                      </tr>
                    ) : (
                      waitlist.map((w, i) => (
                        <tr
                          key={w.email}
                          data-testid={`waitlist-row-${i}`}
                          className="border-b border-crave-border/60 last:border-0"
                        >
                          <td className="px-6 py-4 font-medium text-crave-cream">{w.email}</td>
                          <td className="px-6 py-4 text-crave-muted">
                            {w.joined_at ? new Date(w.joined_at).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-10 rounded-3xl border border-crave-border bg-crave-card p-8">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-crave-gold/15 text-crave-gold">
                  <FlaskConical className="h-5 w-5" aria-hidden="true" />
                </span>
                <h2 className="font-display text-2xl font-medium tracking-tight">Send beta invite</h2>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-crave-muted">
                Paste your Google Play testing link or TestFlight public link. Only testers on the
                chosen platform who haven't received the invite yet will be emailed.
              </p>
              <form onSubmit={sendBeta} className="mt-6 space-y-4">
                <div className="flex gap-3">
                  <PlatformPill
                    active={beta.platform === "android"}
                    onClick={() => setBeta({ ...beta, platform: "android" })}
                    testId="beta-platform-android"
                  >
                    Android
                  </PlatformPill>
                  <PlatformPill
                    active={beta.platform === "ios"}
                    onClick={() => setBeta({ ...beta, platform: "ios" })}
                    testId="beta-platform-ios"
                  >
                    iOS (TestFlight)
                  </PlatformPill>
                </div>
                <label htmlFor="beta-link" className="sr-only">Testing link</label>
                <input
                  id="beta-link"
                  type="url"
                  required
                  value={beta.link}
                  onChange={(e) => setBeta({ ...beta, link: e.target.value })}
                  placeholder={
                    beta.platform === "ios"
                      ? "https://testflight.apple.com/join/…"
                      : "https://play.google.com/apps/testing/…"
                  }
                  data-testid="beta-link-input"
                  className={inputClass}
                />
                <button
                  type="submit"
                  disabled={betaStatus === "loading" || testers.length === 0}
                  data-testid="beta-send-button"
                  className="flex items-center justify-center gap-2 rounded-full bg-crave-gold px-7 py-3.5 text-sm font-bold text-crave-bg transition-colors hover:bg-crave-cream disabled:opacity-60"
                >
                  <Send className="h-4 w-4" aria-hidden="true" />
                  {betaStatus === "loading" ? "Sending…" : "Send beta invite"}
                </button>
                {betaMsg && (
                  <p
                    data-testid="beta-send-result"
                    className={`text-sm ${betaStatus === "error" ? "text-crave-gold" : "text-crave-muted"}`}
                  >
                    {betaMsg}
                  </p>
                )}
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
